import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import StatCard from "@/pages/dashboard/components/StatCard";
import TrendChart from "@/pages/dashboard/components/TrendChart";
import TopDomains from "@/pages/dashboard/components/TopDomains";
import ActivityFeed from "@/pages/dashboard/components/ActivityFeed";
import { useAppData } from "@/hooks/useDomains";
import { isoDate, percentDelta, sumClicks, toTrend, windowStats } from "@/lib/store";

export default function Dashboard() {
  const { domains, stats, activities } = useAppData();
  const navigate = useNavigate();

  const summary = useMemo(() => {
    const total = domains.length;
    const active = domains.filter((item) => item.status === "active").length;
    const clicks = domains.reduce((sum, item) => sum + item.clicks, 0);
    const recent = windowStats(stats, 14);
    const previousWeek = sumClicks(recent.slice(0, 7));
    const thisWeek = sumClicks(recent.slice(7));
    const lastTwo = windowStats(stats, 2);
    const todayClicks = lastTwo[1]?.clicks ?? 0;
    const yesterdayClicks = lastTwo[0]?.clicks ?? 0;
    const monthKey = isoDate(new Date()).slice(0, 7);
    const monthAdded = domains.filter((item) => item.createdAt.startsWith(monthKey)).length;
    return {
      total,
      active,
      clicks,
      todayClicks,
      week: percentDelta(thisWeek, previousWeek),
      today: percentDelta(todayClicks, yesterdayClicks),
      monthAdded,
    };
  }, [domains, stats]);

  const trend = useMemo(() => toTrend(windowStats(stats, 7)), [stats]);
  const top = useMemo(
    () =>
      domains
        .slice()
        .sort((a, b) => b.clicks - a.clicks)
        .slice(0, 5)
        .map((item) => ({ domain: item.domain, clicks: item.clicks })),
    [domains],
  );

  const cards = [
    {
      label: "域名总数",
      value: String(summary.total),
      delta: `本月 +${summary.monthAdded}`,
      trend: "up" as const,
      icon: "ri-global-line",
      tone: "primary" as const,
    },
    {
      label: "运行中",
      value: String(summary.active),
      delta: `${Math.round((summary.active / (summary.total || 1)) * 100)}% 在线`,
      trend: "up" as const,
      icon: "ri-link-m",
      tone: "accent" as const,
    },
    {
      label: "累计点击",
      value: summary.clicks.toLocaleString(),
      delta: summary.week.text,
      trend: summary.week.trend,
      icon: "ri-cursor-line",
      tone: "secondary" as const,
    },
    {
      label: "今日点击",
      value: summary.todayClicks.toLocaleString(),
      delta: summary.today.text,
      trend: summary.today.trend,
      icon: "ri-flashlight-line",
      tone: "primary" as const,
    },
  ];

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col justify-between gap-4 rounded-lg border border-background-200 bg-background-100 p-6 md:flex-row md:items-center">
        <div>
          <h2 className="font-heading text-xl font-semibold text-foreground-950">欢迎回来，管理员</h2>
          <p className="mt-1.5 text-sm text-foreground-600">
            当前有 <span className="font-medium text-primary-700">{summary.active}</span> 个域名正在分发流量，累计点击{" "}
            <span className="font-medium text-primary-700">{summary.clicks.toLocaleString()}</span> 次。
          </p>
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={() => navigate("/domains")}
            className="flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            新增域名
          </button>
          <button
            type="button"
            onClick={() => navigate("/analytics")}
            className="flex items-center gap-2 rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-medium text-foreground-800 transition-colors hover:border-primary-300 cursor-pointer whitespace-nowrap"
          >
            <i className="ri-bar-chart-2-line" />
            查看统计
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        {cards.map((c) => (
          <StatCard key={c.label} {...c} />
        ))}
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-3">
        <div className="xl:col-span-2">
          <TrendChart data={trend} />
        </div>
        <TopDomains items={top} />
      </div>

      <ActivityFeed items={activities} />
    </div>
  );
}